import { useEffect, useRef, useState } from 'react';
import { Alert, Autocomplete, Button, CircularProgress, Grid, IconButton, MenuItem, Stack, Switch, FormControlLabel, TextField, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import { useTranslation } from 'react-i18next';
import { getSpaTreatmentResources, updateSpaTreatmentResources } from '../APIs/SpaAPI';

function QuantityLinks({
  value,
  onChange,
  options,
  idKey,
  label,
  quantityLabel,
  quantityStep = 1,
  disabled,
  optionLabel = (item) => item.name,
}) {
  const { t } = useTranslation();
  const used = value.map((row) => Number(row[idKey]));
  const addable = options.filter((item) => item.active !== false && !used.includes(Number(item.id)));
  return <Stack spacing={1}>
    {value.map((row, index) => {
      const choices = options.filter((item) => Number(item.id) === Number(row[idKey]) || (item.active !== false && !used.includes(Number(item.id))));
      const change = (patch) => onChange(value.map((item, i) => i === index ? { ...item, ...patch } : item));
      return <Grid container spacing={1} alignItems="center" key={index}>
        <Grid item xs={12} sm={7}><TextField select fullWidth size="small" label={label} value={row[idKey]} disabled={disabled}
          onChange={(event) => change({ [idKey]: Number(event.target.value) })}>
          {!choices.some((item) => Number(item.id) === Number(row[idKey])) && <MenuItem value={row[idKey]}>#{row[idKey]}</MenuItem>}
          {choices.map((item) => <MenuItem key={item.id} value={Number(item.id)}>{optionLabel(item)}{item.active === false ? ` (${t('spa.equipment.inactive')})` : ''}</MenuItem>)}
        </TextField></Grid>
        <Grid item xs={8} sm={3}><TextField fullWidth size="small" type="number" label={quantityLabel || t('spa.fields.quantity')} value={row.quantity} disabled={disabled}
          inputProps={{ min: quantityStep, max: 1000000, step: quantityStep }} onChange={(event) => change({ quantity: event.target.value })} /></Grid>
        <Grid item xs={4} sm={2}><Tooltip title={t('spa.equipment.removeLink')}><span><IconButton aria-label={`${t('spa.equipment.removeLink')} ${index + 1}`} disabled={disabled}
          onClick={() => onChange(value.filter((_, i) => i !== index))}><DeleteOutlineIcon /></IconButton></span></Tooltip></Grid>
      </Grid>;
    })}
    {!value.length && <Typography variant="body2" color="text.secondary">{t('spa.equipment.noLinks')}</Typography>}
    <Button startIcon={<AddIcon />} sx={{ alignSelf: 'flex-start' }} disabled={disabled || !addable.length}
      onClick={() => onChange([...value, { [idKey]: Number(addable[0].id), quantity: 1 }])}>{t('spa.equipment.addLink')}</Button>
  </Stack>;
}

export const validEquipmentQuantity = (value) => Number.isInteger(Number(value)) && Number(value) > 0 && Number(value) <= 10000;
export const validConsumableQuantity = (value) => Number.isFinite(Number(value)) && Number(value) > 0 && Number(value) <= 1000000;
export const validEquipmentPlacements = (quantity, placements = []) => validEquipmentQuantity(quantity)
  && placements.every((item) => Number.isInteger(Number(item.roomId)) && Number(item.roomId) > 0 && validEquipmentQuantity(item.quantity))
  && new Set(placements.map((item) => Number(item.roomId))).size === placements.length
  && placements.reduce((sum, item) => sum + Number(item.quantity), 0) <= Number(quantity);

export function SpaEquipmentFields({ draft, setDraft, cabins, disabled }) {
  const { t } = useTranslation();
  const placements = draft.placements || [];
  return <Stack spacing={2}>
    <TextField fullWidth label={t('spa.equipment.description')} multiline minRows={2} value={draft.description || ''} disabled={disabled}
      inputProps={{ maxLength: 500 }} onChange={(event) => setDraft((value) => ({ ...value, description: event.target.value }))} />
    <TextField fullWidth type="number" label={t('spa.fields.quantity')} value={draft.quantity} disabled={disabled}
      inputProps={{ min: 1, max: 10000, step: 1 }} onChange={(event) => setDraft((value) => ({ ...value, quantity: event.target.value }))} />
    <FormControlLabel label={t('spa.equipment.active')} control={<Switch checked={draft.active !== false} disabled={disabled}
      onChange={(event) => setDraft((value) => ({ ...value, active: event.target.checked }))} />} />
    <Typography component="h3" variant="subtitle1" fontWeight={700}>{t('spa.equipment.placements')}</Typography>
    <QuantityLinks options={cabins.map((cabin) => ({ ...cabin, id: Number(cabin.roomId ?? cabin.id) }))} value={placements} idKey="roomId" label={t('spa.fields.cabin')} disabled={disabled}
      onChange={(value) => setDraft((current) => ({ ...current, placements: value }))} />
    {!validEquipmentPlacements(draft.quantity, placements) && <Alert severity="warning">{t('spa.equipment.invalidPlacements')}</Alert>}
  </Stack>;
}

export function SpaTreatmentEquipment({
  treatmentId,
  equipment,
  cabins = [],
  inventoryProducts = [],
  equipmentLoaded,
  inventoryProductsLoaded = false,
  inventoryProductsError = '',
  canViewEquipment,
  canManage,
  token,
  tenant,
  onBusyChange,
}) {
  const { t } = useTranslation();
  const [resources, setResources] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);
  const inFlight = useRef(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    const controller = new AbortController();
    setLoading(true); setResources(null); setError('');
    getSpaTreatmentResources(treatmentId, token, tenant, { signal: controller.signal }).then((data) => {
      if (!data || !Array.isArray(data.equipment)) throw new Error(t('spa.equipment.loadError'));
      if (!controller.signal.aborted) setResources(data);
    }).catch((failure) => { if (!controller.signal.aborted) setError(failure.message); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => { alive.current = false; controller.abort(); };
  }, [treatmentId, token, tenant, reload, t]);
  const validEquipment = (resources?.equipment || []).every((row) => validEquipmentQuantity(row.quantity)
    && equipment.some((item) => Number(item.id) === Number(row.equipmentId) && item.active !== false));
  const validConsumables = (resources?.consumables || []).every((row) => validConsumableQuantity(row.quantity)
    && (!inventoryProductsLoaded || inventoryProducts.some((item) => Number(item.id) === Number(row.productId))));
  const valid = Boolean(validEquipment && validConsumables);
  const save = async () => {
    if (!canManage || !equipmentLoaded || !valid || inFlight.current || !resources) return;
    inFlight.current = true; setBusy(true); onBusyChange(true); setError(''); setSaved(false);
    try {
      // PUT replaces all requirements: preserve every non-equipment resource field.
      const payload = Object.fromEntries(['operatorCount', 'compatibleCabinIds', 'affectedAreaIds', 'requiredOperatorSkills']
        .filter((key) => resources[key] !== undefined).map((key) => [key, resources[key]]));
      payload.equipment = (resources.equipment || []).map((row) => ({ equipmentId: Number(row.equipmentId), quantity: Number(row.quantity) }));
      payload.consumables = (resources.consumables || []).map((row) => ({ productId: Number(row.productId), quantity: Number(row.quantity) }));
      const result = await updateSpaTreatmentResources(treatmentId, payload, token, tenant);
      if (alive.current) { setResources(result); setSaved(true); }
    } catch (failure) { if (alive.current) setError(failure.message); }
    finally { inFlight.current = false; if (alive.current) setBusy(false); onBusyChange(false); }
  };
  return <Stack spacing={1.5}>
    <Typography component="h3" variant="subtitle1" fontWeight={700}>{t('spa.resources.requirements')}</Typography>
    {loading ? <CircularProgress size={22} aria-label={t('spa.common.loading')} /> : <>
      {error && <Alert severity="error" action={!resources && <Button onClick={() => setReload((value) => value + 1)}>{t('spa.equipment.retry')}</Button>}>{error}</Alert>}
      {saved && <Alert severity="success">{t('spa.equipment.requirementsSaved')}</Alert>}
      {!canViewEquipment ? <Alert severity="info">{t('spa.equipment.readDenied')}</Alert> : resources && <>
        <Autocomplete multiple options={cabins.filter((cabin) => cabin.active !== false)}
          value={(resources.compatibleCabinIds || []).map((id) => cabins.find((cabin) => Number(cabin.id) === Number(id)) || { id, name: `#${id}` })}
          getOptionLabel={(cabin) => cabin.name || `#${cabin.id}`} isOptionEqualToValue={(a, b) => Number(a.id) === Number(b.id)}
          disabled={!canManage || busy || !equipmentLoaded} onChange={(_, selected) => { setSaved(false); setResources((current) => ({ ...current, compatibleCabinIds: selected.map((cabin) => Number(cabin.id)) })); }}
          renderInput={(params) => <TextField {...params} label={t('spa.smart.compatibleCabins')} />} />
        <Typography variant="body2" fontWeight={800}>{t('spa.equipment.requirements')}</Typography>
        <QuantityLinks value={resources.equipment} options={equipment} idKey="equipmentId" label={t('spa.catalog.equipment')}
          quantityLabel={t('spa.equipment.requiredQuantity')}
          disabled={!canManage || busy || !equipmentLoaded} onChange={(value) => { setSaved(false); setResources((current) => ({ ...current, equipment: value })); }} />
        {!equipmentLoaded && <Alert severity="warning">{t('spa.equipment.loadError')}</Alert>}
        {equipmentLoaded && !equipment.length && <Alert severity="info">{t('spa.equipment.empty')}</Alert>}
        {equipmentLoaded && !validEquipment && <Alert severity="warning">{t('spa.equipment.invalidRequirements')}</Alert>}
        <Typography variant="body2" fontWeight={800}>{t('spa.products.consumables')}</Typography>
        <QuantityLinks value={resources.consumables || []} options={inventoryProducts} idKey="productId" label={t('spa.products.product')}
          quantityLabel={t('spa.products.plannedQuantity')} quantityStep={0.01}
          optionLabel={(item) => `${item.name} (${t(`spa.products.units.${item.unit}`, { defaultValue: item.unit })})`}
          disabled={!canManage || busy || !inventoryProductsLoaded}
          onChange={(value) => { setSaved(false); setResources((current) => ({ ...current, consumables: value })); }} />
        {inventoryProductsError && <Alert severity="warning">{inventoryProductsError}</Alert>}
        {inventoryProductsLoaded && !inventoryProducts.length && <Alert severity="info">{t('spa.products.empty')}</Alert>}
        {inventoryProductsLoaded && !validConsumables && <Alert severity="warning">{t('spa.products.invalidConsumables')}</Alert>}
        <Button variant="outlined" startIcon={busy ? <CircularProgress size={16} /> : <SaveIcon />} onClick={save}
          disabled={!canManage || busy || !equipmentLoaded || !valid} sx={{ alignSelf: 'flex-start' }}>{t('spa.resources.saveRequirements')}</Button>
      </>}
    </>}
  </Stack>;
}
